import { useState } from "react";
import { CrudTable, StatusBadge, Rating, Card } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DailyWorkCard from "@/components/DailyWorkCard";
import { ComplaintButton } from "@/components/QuickActions";

const STAGES = [
  "1. Telecaller Enquiry", "2. Clinic Walk-in", "3. Doctor Consultation",
  "4. Post-Visit", "5. Pre-Operation", "6. Post-Operation", "7. Post-Op Follow-up",
];
const SERVICES = ["Rhinoplasty", "Hair Transplant", "Liposuction", "Botox", "Dermal Fillers", "Laser Skin", "Facelift", "Gynecomastia"];
const YesNo = ({ v }) => <span className={v ? "text-[#34d399]" : "text-slate-500"}>{v ? "Yes" : "No"}</span>;

function Kanban({ rows }) {
  return (
    <div className="overflow-x-auto pb-4 mb-6">
      <div className="flex gap-4 min-w-max">
        {STAGES.map((stage) => {
          const items = rows.filter((r) => r.current_stage === stage);
          return (
            <div key={stage} data-testid="kanban-column" className="w-64 shrink-0">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-xs text-slate-300 font-medium">{stage.replace(/^\d+\.\s/, "")}</span>
                <span className="text-xs text-[#C9A24B] font-mono-ync">{items.length}</span>
              </div>
              <div className="space-y-2">
                {items.map((r) => (
                  <Card key={r.id} data-testid="kanban-card" className="p-3 hover:border-[#C9A24B]/30 transition-colors cursor-default">
                    <div className="text-sm text-white font-medium">{r.patient_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{r.service_interested}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] text-slate-500">{r.branch}</span>
                      {r.satisfaction_score && <span className="text-[10px] font-mono-ync text-[#34d399]">{r.satisfaction_score}/10</span>}
                    </div>
                  </Card>
                ))}
                {items.length === 0 && <div className="text-xs text-slate-600 py-4 text-center border border-dashed border-white/5 rounded-lg">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PatientJourney() {
  const [view, setView] = useState("table");
  const [rows, setRows] = useState([]);

  const columns = [
    { key: "date", label: "Date", render: (r) => r.date?.slice(0, 10) },
    { key: "branch", label: "Branch" },
    { key: "patient_name", label: "Patient" },
    { key: "service_interested", label: "Service" },
    { key: "current_stage", label: "Stage", render: (r) => <span className="text-slate-200">{r.current_stage}</span> },
    { key: "telecaller_response_time", label: "TC Resp (min)" },
    { key: "walkin_wait_time", label: "Wait (min)" },
    { key: "reception_rating", label: "Reception", render: (r) => <Rating value={r.reception_rating} /> },
    { key: "doctor_rating", label: "Doctor", render: (r) => <Rating value={r.doctor_rating} /> },
    { key: "converted", label: "Converted", render: (r) => <YesNo v={r.converted} /> },
    { key: "preop_counseling_done", label: "Pre-Op", render: (r) => <YesNo v={r.preop_counseling_done} /> },
    { key: "postop_followup_done", label: "Post-Op FU", render: (r) => <YesNo v={r.postop_followup_done} /> },
    { key: "satisfaction_score", label: "Satisfaction", render: (r) => r.satisfaction_score ? <span className="font-mono-ync text-[#34d399]">{r.satisfaction_score}/10</span> : "—" },
  ];
  const fields = [
    { key: "patient_name", label: "Patient Name", type: "text" },
    { key: "branch", label: "Branch", type: "select", optionsKey: "branches" },
    { key: "service_interested", label: "Service Interested", type: "select", options: SERVICES },
    { key: "current_stage", label: "Current Stage", type: "select", options: STAGES },
    { key: "walkin_wait_time", label: "Walk-in Wait Time (min)", type: "number" },
    { key: "reception_rating", label: "Reception Rating (1-5)", type: "number", min: 1, max: 5, step: 1 },
    { key: "doctor_rating", label: "Doctor Consultation Rating (1-5)", type: "number", min: 1, max: 5, step: 1 },
    { key: "followup_call_date", label: "Follow-up Call Date", type: "date" },
    { key: "satisfaction_score", label: "Patient Satisfaction Score (1-10)", type: "number", min: 1, max: 10, step: 1 },
    { key: "converted", label: "Converted to Procedure", type: "switch" },
    { key: "preop_counseling_done", label: "Pre-Op Counseling Done", type: "switch" },
    { key: "postop_followup_done", label: "Post-Op Follow-up Done", type: "switch" },
  ];

  return (
    <>
      <DailyWorkCard />
      <CrudTable
      testid="journey" endpoint="patients" dateField="date"
      title="Patient Journey Tracker"
      subtitle="The single source of truth — every patient's full journey from enquiry to 90-day follow-up."
      columns={columns} fields={fields}
      defaults={{ current_stage: STAGES[0], converted: false, preop_counseling_done: false, postop_followup_done: false }}
      onData={setRows}
      extraTop={
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="bg-[#1E293B] border border-white/5">
                <TabsTrigger value="table" data-testid="journey-view-table" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0F172A]">Table</TabsTrigger>
                <TabsTrigger value="kanban" data-testid="journey-view-kanban" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0F172A]">Pipeline / Kanban</TabsTrigger>
              </TabsList>
            </Tabs>
            <ComplaintButton />
          </div>
          {view === "kanban" && <div className="mt-4"><Kanban rows={rows} /></div>}
        </div>
      }
      />
    </>
  );
}
